import { cache } from 'react';
import { getNativeAppStructure } from '@/lib/native-app-data';
import type { HomePageConfig } from '@/types/aggregation/home';
import type { NativeRouteDocument, NativeWebShell } from '@/types/aggregation/native-app-data';
import { adaptNativeAppData, adaptPageConfigAppData, getMissingNativeAppDataFields } from './app-data-adapters';
import { getAppRouteKind, getAppRouteSlug, isNativeApiAllowedRoute } from './app-data-route';
import type { ResolvedAppData } from './app-data.types';
import { getAppDataSource } from './get-app-data-source';
import { logAppDataSource, type AppDataFallback } from './log-app-data-source';
import { checkAndThrowIfNotFound } from '@/utils/not-found/not-found';

const fetchPageConfigFallback = cache(async (currentRoute: string): Promise<HomePageConfig | undefined> => {
    const { getPageConfig } = await import('@/lib/aggregation');
    return getPageConfig(currentRoute);
});

const logNativeFallback = (route: string, reason: string, error?: unknown) => {
    console.error(
        `========== [APP DATA] NATIVE API FALLBACK ==========\nRoute: ${route}\nReason: ${reason}\nUsing PageConfig for the entire request.\n=====================================================`,
        error ?? ''
    );
};

type NativeCategoryBundle = {
    structure: NativeWebShell;
    routeDocument: NativeRouteDocument;
};

const getNativeCategoryBundle = async (currentRoute: string): Promise<NativeCategoryBundle | null> => {
    const { getCategory } = await import('@/logic/page-config/category/category');
    const slug = getAppRouteSlug(currentRoute);
    const category = await getCategory(slug);
    const shell = category.nativeShell;
    const productData = shell?.productData;
    if (!category.uid || (!category.categoryName && category.zones.length === 0) || !shell || !productData) {
        return null;
    }
    const { nativeShell: _nativeShell, ...categoryPage } = category;

    return {
        structure: {
            header: shell.header,
            footer: shell.footer,
            top_navigation: productData.top_navigation,
            bottom_bar: productData.bottom_bar,
            theme_name: productData.theme_name,
            bonus_draw: productData.bonus_draw,
            product_features: productData.product_features,
            site_wide_jackpot: productData.site_wide_jackpot,
            logout_popup: productData.logout_popup,
            app: productData.app,
            rewards_machine_content: productData.rewards_machine_content,
            region_info: shell.regionInfo,
            experiments: shell.experiments
        },
        routeDocument: {
            uid: category.uid,
            content: {
                category_name: category.categoryName,
                show_filters: category.canFilter,
                description: category.seoDescription,
                seo_category_description_headline: category.seoHeadline
            },
            zones: category.zones,
            route_data: { category: categoryPage },
            seo_meta_title: category.seoMetaTitle,
            seo_meta_description: category.seoMetaDescription,
            homepage_description: category.seoDescription,
            homepage_description_headline: category.seoHeadline
        }
    };
};

const resolveUncachedAppData = async (currentRoute: string): Promise<ResolvedAppData | null> => {
    const route = currentRoute || '/';
    const routeKind = getAppRouteKind(route);
    const selectedAppDataSource = await getAppDataSource();
    const appDataSource =
        selectedAppDataSource === 'native-api' && !isNativeApiAllowedRoute(route)
            ? 'page-config'
            : selectedAppDataSource;

    // Carried to the Datadog log so a native arm that broke is distinguishable both from a route
    // that was never in the rollout and from a user the canary never put on native at all.
    let fallback: AppDataFallback | undefined =
        appDataSource === selectedAppDataSource
            ? undefined
            : { kind: 'route-disabled', reason: 'Route is not native-api enabled.' };

    if (appDataSource === 'native-api') {
        try {
            const isCategoryRoute = routeKind === 'category';
            const categoryBundle = isCategoryRoute ? await getNativeCategoryBundle(route) : null;
            const [structure, routeDocument] =
                routeKind === 'home'
                    ? [await getNativeAppStructure(route), null]
                    : [categoryBundle?.structure ?? null, categoryBundle?.routeDocument ?? null];
            const nativeAppData = adaptNativeAppData(route, routeKind, structure, routeDocument);
            if (nativeAppData) {
                await logAppDataSource({ route, routeKind, source: 'native-api' });
                return nativeAppData;
            }
            const missingFields = getMissingNativeAppDataFields(routeKind, structure, routeDocument);
            fallback = {
                kind: 'native-failed',
                reason: missingFields.length
                    ? `Native response was missing: ${missingFields.join(', ')}.`
                    : 'Native response could not be adapted.'
            };
            logNativeFallback(route, fallback.reason);
        } catch (error) {
            fallback = { kind: 'native-failed', reason: 'Native request or adaptation failed.' };
            logNativeFallback(route, fallback.reason, error);
        }
    }

    const pageConfig = await fetchPageConfigFallback(route);
    checkAndThrowIfNotFound(pageConfig ?? {});
    // After the not-found check, so a thrown 404 is not logged as a page a user was served.
    await logAppDataSource({ route, routeKind, source: 'page-config', fallback });
    return adaptPageConfigAppData(
        route,
        routeKind,
        pageConfig as unknown as Parameters<typeof adaptPageConfigAppData>[2]
    );
};

export const resolveAppData = cache(resolveUncachedAppData);
